import { ExclamationCircleFilled } from '@ant-design/icons'
import { faCalendar, faPills, faPlus, faStethoscope, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Modal as AntModal,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Select,
  Space,
  Typography,
  DatePicker as AntDatePicker
} from 'antd'
import dayjs from 'dayjs'
import React, { useState, useEffect, useRef } from 'react'
import DesktopMedicalHistoryNav from './Nav'
// Removed mobile DatePicker import - using Ant Design DatePicker instead
import useChronicIllnesses from '../../hooks/useChronicIllnesses'
import '../style/DesktopChronicIllnesses.css'
import DesktopSectionHeader from './DesktopSectionHeader'

const DesktopChronicIllnesses = ({ clientForm, data, setFacet, setSaving }) => {
  const { Text } = Typography
  const {
    illnessList,
    handleAddIllness,
    handleRemoveIllness,
    handleUpdateIllness,
    handleRemoveMedication,
    handleAddMedication,
    handleUpdateMedication,
    nappiData,
    icd10Data,
    nappiLoading,
    icd10Loading,
    handleSubmit,
    setICD10SearchFilter,
    setNappiSearchFilter,
    showIllnessModal,
    setShowIllnessModal,
    medicationModalState,
    setMedicationModalState,
    editingIllness,
    setEditingIllness,
    tempDiagnosisDate,
    setTempDiagnosisDate,
    tempMedDates,
    setTempMedDates
  } = useChronicIllnesses({ clientForm, setFacet, setSaving })

  // Using Ant Design DatePicker directly - no need for show/hide state
  const [selectedCondition, setSelectedCondition] = useState(null)
  const [selectedMedication, setSelectedMedication] = useState(null)
  const [validationErrors, setValidationErrors] = useState({
    condition: false,
    diagnosisDate: false
  })

  // Unsaved changes modal state
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)
  const initialState = useRef(null)

  // Capture initial state when component loads - with delay to ensure state is loaded
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!initialState.current && illnessList) {
        initialState.current = {
          illnessList: JSON.parse(JSON.stringify(illnessList))
        }
        console.log('Initial chronic illnesses state captured:', initialState.current)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [illnessList])

  // Clear initial state when component unmounts
  useEffect(() => {
    return () => {
      initialState.current = null
    }
  }, [])

  // Scroll to top when component mounts
  useEffect(() => {
    setTimeout(() => {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }, 0)
  }, [])

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (!initialState.current) return false

    const hasChanges = JSON.stringify(illnessList) !== JSON.stringify(initialState.current.illnessList)
    console.log('Chronic illness change detection:', {
      hasChanges,
      initial: initialState.current.illnessList,
      current: illnessList
    })

    return hasChanges
  }

  // Handle navigation with unsaved changes check
  const handleNavigation = (navigationFunction) => {
    if (hasUnsavedChanges()) {
      setPendingNavigation(() => navigationFunction)
      setShowUnsavedModal(true)
    } else {
      navigationFunction()
    }
  }

  // Update initial state after successful save
  const updateInitialStateAfterSave = () => {
    initialState.current = {
      illnessList: JSON.parse(JSON.stringify(illnessList))
    }
    console.log('Initial state updated after save:', initialState.current)
  }

  // Wrapper for handleSubmit that also updates initial state
  const handleSaveWithStateUpdate = async () => {
    try {
      await handleSubmit()
      updateInitialStateAfterSave()
    } catch (error) {
      // Error is already handled in handleSubmit
    }
  }

  // Handle saving and then navigating
  const handleSaveAndNavigate = async () => {
    try {
      await handleSubmit()
      updateInitialStateAfterSave()
      setShowUnsavedModal(false)
      if (pendingNavigation) {
        pendingNavigation()
        setPendingNavigation(null)
      }
    } catch (error) {
      // Error is already handled in handleSubmit
    }
  }

  // Handle navigation without saving - cannot restore chronic illnesses state easily
  // so we just navigate without saving
  const handleNavigateWithoutSaving = () => {
    setShowUnsavedModal(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  // Helper function to convert text to title case
  const toTitleCase = (str) => {
    if (!str) return str
    return str.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())
  }

  const showDeleteIllnessConfirm = (illness, index) => {
    AntModal.confirm({
      title: 'Remove Chronic Illness',
      icon: <ExclamationCircleFilled style={{ color: '#FF4D4F' }} />,
      content: (
        <div>
          <p>
            Are you sure you want to remove <strong>{illness.condition}</strong>?
          </p>
          <p>This action will also remove all associated medications.</p>
        </div>
      ),
      okText: 'Remove',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk() {
        handleRemoveIllness(index)
      }
    })
  }

  const showDeleteMedicationConfirm = (medication, medicationIndex, illnessIndex) => {
    AntModal.confirm({
      title: 'Remove Medication',
      icon: <ExclamationCircleFilled style={{ color: '#FF4D4F' }} />,
      content: (
        <div>
          <p>
            Are you sure you want to remove <strong>{medication.med.name}</strong>?
          </p>
        </div>
      ),
      okText: 'Remove',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk() {
        handleRemoveMedication(medicationIndex, illnessIndex)
      }
    })
  }

  return (
    <div className='desktop-chronic-illnesses-container'>
      <DesktopSectionHeader
        icon={faStethoscope}
        title='Chronic Illnesses'
        subtitle='Track your ongoing medical conditions and medications'
        gradient='linear-gradient(135deg, #9267E5 0%, #A783F8 100%)'
      />

      <div className='desktop-chronic-illnesses-content'>
        {/* Illnesses List with Add Button */}
        <Row gutter={[24, 40]}>
          {illnessList.map((illness, illnessIndex) => (
            <Col xs={24} lg={12} key={illnessIndex}>
              <Card className='desktop-illness-card'>
                <div className='desktop-illness-header'>
                  <div className='desktop-illness-info'>
                    <div className='desktop-illness-icon'>
                      <FontAwesomeIcon icon={faStethoscope} />
                    </div>
                    <div>
                      <h3 className='desktop-illness-name'>{toTitleCase(illness.condition || illness.description)}</h3>
                      <Text className='desktop-diagnosis-date'>
                        Diagnosed:{' '}
                        {illness.dateDiagnosed ? dayjs(illness.dateDiagnosed).format('DD/MM/YYYY') : 'Unknown'}
                      </Text>
                    </div>
                  </div>
                  <Button
                    danger
                    type='text'
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={() => showDeleteIllnessConfirm(illness, illnessIndex)}
                    className='desktop-remove-illness-btn'
                  />
                </div>

                {/* Medications for this illness */}
                <div className='desktop-medications-section'>
                  <div className='desktop-medications-header'>
                    <h4>Chronic Medication</h4>
                    <Button
                      type='primary'
                      icon={<FontAwesomeIcon icon={faPlus} />}
                      onClick={() =>
                        setMedicationModalState({
                          show: true,
                          index: illnessIndex,
                          isEditing: false
                        })
                      }
                      size='small'
                    >
                      Add Medication
                    </Button>
                  </div>

                  {illness.medications && illness.medications.length > 0 ? (
                    <div className='desktop-medications-list'>
                      {illness.medications.map((medication, medIndex) => (
                        <div key={medIndex} className='desktop-medication-item'>
                          <div className='desktop-medication-info'>
                            <div className='desktop-medication-text'>
                              <div className='desktop-med-name-row'>
                                <FontAwesomeIcon icon={faPills} className='desktop-med-icon' />
                                <span className='desktop-med-name'>{toTitleCase(medication.med.name)}</span>
                              </div>
                              <div className='desktop-med-dates'>
                                <Text type='secondary'>
                                  {medication.startDate
                                    ? dayjs(medication.startDate).format('MMM YYYY')
                                    : 'Start: Unknown'}{' '}
                                  -{medication.endDate ? dayjs(medication.endDate).format('MMM YYYY') : ' Ongoing'}
                                </Text>
                              </div>
                            </div>
                          </div>
                          <Button
                            danger
                            type='text'
                            size='small'
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            onClick={() => showDeleteMedicationConfirm(medication, medIndex, illnessIndex)}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Text type='secondary' className='desktop-no-medications'>
                      No medications added for this condition
                    </Text>
                  )}
                </div>
              </Card>
            </Col>
          ))}

          {/* Add New Illness Button - appears to the right of the last illness card */}
          {illnessList.length > 0 && (
            <Col xs={24} lg={12}>
              <Card className='desktop-add-illness-card'>
                <Button
                  type='primary'
                  icon={<FontAwesomeIcon icon={faPlus} />}
                  onClick={() => {
                    // Reset all form state when opening modal
                    setSelectedCondition(null)
                    setTempDiagnosisDate(null)
                    setValidationErrors({ condition: false, diagnosisDate: false })
                    setShowIllnessModal(true)
                  }}
                  size='large'
                  className='desktop-add-illness-btn'
                >
                  Add Chronic Illness
                </Button>
              </Card>
            </Col>
          )}
        </Row>

        {/* Empty State Message with Add Button */}
        {illnessList.length === 0 && (
          <>
            <Card className='desktop-empty-state'>
              <div className='desktop-empty-content'>
                <FontAwesomeIcon icon={faStethoscope} className='desktop-empty-icon' />
                <h3>No Chronic Illnesses Added</h3>
                <p>Click "Add Chronic Illness" below to start tracking your ongoing medical conditions.</p>
              </div>
            </Card>

            {/* Add New Illness Button - appears below empty state */}
            <Card className='desktop-add-illness-card'>
              <Button
                type='primary'
                icon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => {
                  // Reset all form state when opening modal
                  setSelectedCondition(null)
                  setTempDiagnosisDate(null)
                  setValidationErrors({ condition: false, diagnosisDate: false })
                  setShowIllnessModal(true)
                }}
                size='large'
                className='desktop-add-illness-btn'
              >
                Add Chronic Illness
              </Button>
            </Card>
          </>
        )}
      </div>

      {/* Add/Edit Illness Modal */}
      <Modal
        title={editingIllness !== null ? 'Edit Chronic Illness' : 'Add Chronic Illness'}
        open={showIllnessModal}
        onCancel={() => {
          setShowIllnessModal(false)
          setEditingIllness(null)
          setSelectedCondition(null)
          setTempDiagnosisDate(null)
          setValidationErrors({ condition: false, diagnosisDate: false })
        }}
        footer={null}
        className='desktop-illness-modal'
      >
        <div className='desktop-modal-content'>
          <div className='desktop-form-field'>
            <label>Condition Name</label>
            <Select
              showSearch
              placeholder='Search for a condition...'
              style={{ width: '100%' }}
              loading={icd10Loading}
              onSearch={setICD10SearchFilter}
              filterOption={false}
              notFoundContent={icd10Loading ? 'Searching...' : 'No conditions found'}
              value={selectedCondition?.description}
              onSelect={(value, option) => {
                const conditionObj = icd10Data.find((item) => item.description === value)
                setSelectedCondition(conditionObj || { description: value, code: value })
                console.log('Selected condition:', conditionObj || { description: value, code: value })
              }}
            >
              {icd10Data.map((item, index) => (
                <Select.Option key={index} value={item.description}>
                  {item.description}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className='desktop-form-field'>
            <label>Diagnosis Date</label>
            <AntDatePicker
              placeholder='Select diagnosis date'
              value={tempDiagnosisDate ? dayjs(tempDiagnosisDate) : null}
              onChange={(date) => {
                console.log('Date selected:', date)
                setTempDiagnosisDate(date ? date.format('YYYY-MM-DD') : null)
              }}
              disabledDate={(current) => current && current > dayjs().endOf('day')}
              style={{ width: '100%', height: '40px' }}
              format='YYYY-MM-DD'
            />
          </div>

          <div className='desktop-modal-actions'>
            <Button onClick={() => setShowIllnessModal(false)}>Cancel</Button>
            <Button
              type='primary'
              onClick={() => {
                if (selectedCondition && tempDiagnosisDate) {
                  console.log('Adding illness with:', { condition: selectedCondition, date: tempDiagnosisDate })
                  handleAddIllness(selectedCondition)
                } else {
                  console.log('Missing data:', { selectedCondition, tempDiagnosisDate })
                  if (!selectedCondition) setValidationErrors((prev) => ({ ...prev, condition: true }))
                  if (!tempDiagnosisDate) setValidationErrors((prev) => ({ ...prev, diagnosisDate: true }))
                }
              }}
            >
              {editingIllness !== null ? 'Update' : 'Add'} Illness
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add/Edit Medication Modal */}
      <Modal
        title='Add Medication'
        open={medicationModalState.show}
        onCancel={() => setMedicationModalState({ show: false })}
        footer={null}
        className='desktop-medication-modal'
      >
        <div className='desktop-modal-content'>
          <div className='desktop-form-field'>
            <label>Medication Name</label>
            <Select
              showSearch
              placeholder='Search for medication...'
              style={{ width: '100%' }}
              loading={nappiLoading}
              onSearch={setNappiSearchFilter}
              filterOption={false}
              notFoundContent={nappiLoading ? 'Searching...' : 'No medications found'}
              value={selectedMedication?.name || selectedMedication?.genericName}
              onSelect={(value, option) => {
                const medObj = nappiData.find(
                  (med) => (med.name && med.name === value) || (med.genericName && med.genericName === value)
                )
                setSelectedMedication(medObj || { name: value, genericName: value })
                console.log('Selected medication:', medObj || { name: value, genericName: value })
                console.log('Available nappiData:', nappiData)
              }}
            >
              {nappiData && nappiData.length > 0
                ? nappiData.map((med, index) => (
                    <Select.Option
                      key={index}
                      value={med.name || med.genericName || med.description || `medication-${index}`}
                    >
                      {med.name || med.genericName || med.description || `Medication ${index + 1}`}
                    </Select.Option>
                  ))
                : null}
            </Select>
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <div className='desktop-form-field'>
                <label>Start Date</label>
                <AntDatePicker
                  placeholder='Select start date'
                  value={tempMedDates.startDate ? dayjs(tempMedDates.startDate) : null}
                  onChange={(date) => {
                    setTempMedDates((prev) => ({
                      ...prev,
                      startDate: date ? date.format('YYYY-MM-DD') : null
                    }))
                  }}
                  disabledDate={(current) => current && current > dayjs().endOf('day')}
                  style={{ width: '100%', height: '40px' }}
                  format='YYYY-MM-DD'
                />
              </div>
            </Col>
            <Col span={12}>
              <div className='desktop-form-field'>
                <label>End Date (Optional)</label>
                <AntDatePicker
                  placeholder='Ongoing'
                  value={tempMedDates.endDate ? dayjs(tempMedDates.endDate) : null}
                  onChange={(date) => {
                    setTempMedDates((prev) => ({
                      ...prev,
                      endDate: date ? date.format('YYYY-MM-DD') : null
                    }))
                  }}
                  disabledDate={(current) => current && current > dayjs().endOf('day')}
                  style={{ width: '100%', height: '40px' }}
                  format='YYYY-MM-DD'
                  allowClear
                />
              </div>
            </Col>
          </Row>

          <div className='desktop-modal-actions'>
            <Button
              onClick={() => {
                setMedicationModalState({ show: false })
                setSelectedMedication(null)
              }}
            >
              Cancel
            </Button>
            <Button
              type='primary'
              onClick={() => {
                if (selectedMedication) {
                  console.log('Adding medication:', {
                    medication: selectedMedication,
                    dates: tempMedDates,
                    illnessIndex: medicationModalState.index,
                    illnessList: illnessList
                  })

                  // Validate that we have a valid illness index
                  if (medicationModalState.index !== undefined && illnessList[medicationModalState.index]) {
                    handleAddMedication(selectedMedication, tempMedDates)
                    setSelectedMedication(null)
                  } else {
                    console.error('Invalid illness index:', medicationModalState.index, 'illnessList:', illnessList)
                  }
                } else {
                  console.log('Missing medication selection:', { selectedMedication, tempMedDates })
                }
              }}
            >
              Add Medication
            </Button>
          </div>
        </div>
      </Modal>

      {/* Using Ant Design DatePickers inline - no separate components needed */}

      {/* Unsaved Changes Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FontAwesomeIcon icon={faStethoscope} style={{ color: '#ff6b6b' }} />
            <span>Unsaved Changes</span>
          </div>
        }
        open={showUnsavedModal}
        onCancel={() => setShowUnsavedModal(false)}
        footer={null}
        className='desktop-unsaved-changes-modal'
        centered
      >
        <div className='desktop-unsaved-changes-content'>
          <Typography.Text className='desktop-unsaved-changes-message'>
            You have unsaved changes to your chronic illness information. What would you like to do?
          </Typography.Text>

          <div className='desktop-unsaved-changes-actions'>
            <Button type='primary' className='desktop-save-changes-btn' onClick={handleSaveAndNavigate} loading={false}>
              Save Changes
            </Button>
            <Button type='default' className='desktop-discard-changes-btn' onClick={handleNavigateWithoutSaving}>
              Discard Changes
            </Button>
            <Button type='text' className='desktop-cancel-modal-btn' onClick={() => setShowUnsavedModal(false)}>
              Continue Editing
            </Button>
          </div>
        </div>
      </Modal>

      <DesktopMedicalHistoryNav
        prevFunction={() => handleNavigation(() => setFacet(null))}
        nextFunction={handleSaveWithStateUpdate}
        prevText='Back to Overview'
        nextText='Save Chronic Illnesses'
      />
    </div>
  )
}

export default DesktopChronicIllnesses
